import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import type { ToolLibraryRecord, ToolSlotResponse } from '@bridgewerk/shared';
import { Button } from '../../../components/ui/Button.js';
import { Skeleton } from '../../../components/ui/Skeleton.js';
import { useToast } from '../../../components/ui/Toast.js';
import { useAssignToolToSlot, useToolLibrary } from '../../tooling/hooks/useToolLibrary.js';

export interface AssignToolModalProps {
  readonly shopId: string;
  readonly machineId: string;
  readonly slot: ToolSlotResponse;
  readonly onClose: () => void;
}

export function AssignToolModal({
  shopId,
  machineId,
  slot,
  onClose,
}: AssignToolModalProps): JSX.Element {
  const toast = useToast();
  const library = useToolLibrary(shopId);
  const assign = useAssignToolToSlot(shopId);
  const [selected, setSelected] = useState<string | null>(slot.tool_definition?.tool_id ?? null);

  // Close on Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleAssign = async () => {
    if (!selected) return;
    try {
      await assign.mutateAsync({
        machineId,
        toolNumber: slot.tool_number,
        toolLibraryId: selected,
      });
      toast.push({
        tone: 'success',
        title: 'Tool assigned',
        description: `Slot T${String(slot.tool_number).padStart(2, '0')} → ${library.data?.find((l) => l.tool_id === selected)?.description ?? 'updated'}`,
      });
      onClose();
    } catch (err) {
      toast.push({
        tone: 'critical',
        title: 'Assignment failed',
        description: err instanceof Error ? err.message : String(err),
      });
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="assign-tool-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-lg rounded-md border border-border bg-surface shadow-xl">
        <header className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div>
            <h2 id="assign-tool-title" className="font-mono text-xs uppercase tracking-[0.2em] text-text-dim">
              Assign tool to slot
            </h2>
            <p className="mt-1 text-sm">
              <span className="font-mono">{machineId}</span>
              <span className="mx-2 text-text-dim">·</span>
              <span className="font-mono">T{String(slot.tool_number).padStart(2, '0')}</span>
              <span className="mx-2 text-text-dim">·</span>
              <span className="font-mono text-text-dim">{slot.life_percent}% remaining</span>
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-dim hover:text-text leading-none px-2"
          >
            ✕
          </button>
        </header>

        <div className="max-h-80 overflow-y-auto">
          {library.isLoading && (
            <div className="space-y-2 p-4">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          )}

          {library.isError && (
            <div className="p-6 text-center text-xs text-text-dim font-mono">
              <p>Failed to load tool library.</p>
              <button onClick={library.refetch} className="mt-2 text-accent underline">
                Retry
              </button>
            </div>
          )}

          {!library.isLoading && !library.isError && library.data?.length === 0 && (
            <div className="p-6 text-center text-xs text-text-dim font-mono">
              <p>No tools in the library yet.</p>
              <Link to="/tooling" onClick={onClose} className="mt-2 inline-block text-accent underline">
                Add the first tool →
              </Link>
            </div>
          )}

          {!library.isLoading && !library.isError && library.data && library.data.length > 0 && (
            <ul className="divide-y divide-border">
              {library.data.map((entry) => (
                <li key={entry.tool_id}>
                  <LibraryRow
                    entry={entry}
                    selected={selected === entry.tool_id}
                    onSelect={() => setSelected(entry.tool_id)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="px-4 py-3 border-t border-border flex items-center justify-between gap-2">
          <Link to="/tooling" onClick={onClose} className="text-xs text-accent underline">
            Manage library →
          </Link>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onClose} disabled={assign.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleAssign}
              disabled={!selected || assign.isPending || selected === slot.tool_definition?.tool_id}
            >
              {assign.isPending ? 'Assigning…' : 'Assign'}
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function LibraryRow({
  entry,
  selected,
  onSelect,
}: {
  readonly entry: ToolLibraryRecord;
  readonly selected: boolean;
  readonly onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={[
        'w-full text-left px-4 py-3 transition-colors',
        selected ? 'bg-card border-l-2 border-l-accent' : 'border-l-2 border-l-transparent hover:bg-card/50',
      ].join(' ')}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm truncate">{entry.description}</div>
          <div className="text-[10px] font-mono text-text-dim">
            {entry.type} · {entry.material}
            {entry.diameter_mm != null && ` · ${entry.diameter_mm} mm`}
            {entry.part_number && ` · PN ${entry.part_number}`}
          </div>
        </div>
        {selected && (
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-accent">
            Selected
          </span>
        )}
      </div>
    </button>
  );
}
